<template>
  <ul class="h-full">
    <li v-for="item in featureListFiltered" :key="item.id" class="mb-0.5">
      <template v-if="isMainFeatureEnable(item)">
        <!-- Parent with children -->
        <div v-if="item.childs?.length && item.role?.includes(activeRole)">
          <div
            :class="[
              makeDropdownActive(item),
              item.showChilds && leftBarVisibility ? 'rounded-t-md' : 'rounded-md',
            ]"
            class="p-2 flex items-center xl:py-4 py-3 justify-between cursor-pointer"
            @click="toggleChilds(item)"
            @mouseover="() => (currentHoverItem = item)"
            @mouseleave="() => (currentHoverItem = '')"
          >
            <span class="flex gap-2 items-center justify-center cursor-pointer">
              <icon
                class="icon"
                :icon="item.icon"
                :color="setIconColor(item)"
                height="20"
                width="20"
              />
              <h2 v-if="leftBarVisibility" class="font-inter font-medium text-sm select-none">
                {{ $t(`leftBar.${item.name}`) }}
              </h2>
            </span>
            <icon
              v-if="leftBarVisibility"
              class="icon"
              :icon="item.showChilds ? 'chevronUp' : 'chevronBottom'"
              :color="setIconColor(item)"
              height="5.801"
              width="10.146"
            />
          </div>

          <!-- Children -->
          <transition name="slide">
            <ul
              v-if="item.showChilds && leftBarVisibility"
              class="dropdown bg-primary-purple-25 pb-[15px] rounded-b-md"
            >
              <li
                v-for="child in item.childs"
                :key="child.name"
                class="cursor-pointer font-inter font-medium text-sm"
                :class="childItemsClasses(item)"
              >
                <!-- Router child -->
                <router-link
                  v-if="childRoleAccess(child) && isSubFeatureEnable(item, child)"
                  v-slot="{ isActive, isExactActive }"
                  :to="child.route"
                >
                  <li class="py-[6px] block ml-4 border-l border-purple-800" @click="toggleLeftBar">
                    <div class="justify-start gap-2 flex items-center">
                      <div class="w-[14px] border-t border-purple-800"></div>
                      <a
                        :class="[
                          child.metaData
                            ? isActive && childItemActive(item)
                            : isExactActive && childItemActive(item),
                        ]"
                      >
                        {{ $t(`leftBar.${child.displayName || child.name}`) }}
                      </a>
                    </div>
                  </li>
                </router-link>

                <!-- Dispatch action child -->
                <h1
                  v-else-if="child.click"
                  class="p-2 block"
                  @click="$store.dispatch(child.click.actionName)"
                >
                  {{ $t(`leftBar.${child.name}`) }}
                </h1>
              </li>
            </ul>
          </transition>
        </div>

        <!-- Item without children -->
        <template v-else>
          <router-link v-if="showItems(item)" v-slot="{ route, isExactActive }" :to="item.route">
            <div
              @mouseover="() => (currentHoverItem = item)"
              @mouseleave="() => (currentHoverItem = '')"
              @click.prevent="closeLeftBar(route, item)"
            >
              <span
                class="flex gap-2 xl:py-4 py-3 rounded-md px-2 items-center justify-start cursor-pointer"
                :class="setTextColor(isExactActive, item)"
              >
                <icon
                  class="icon"
                  :icon="item.icon"
                  :color="setIconColor(item, isExactActive)"
                  height="20"
                  width="20"
                />
                <a
                  v-if="showLeftBarItem(item)"
                  class="font-inter font-medium rounded-md text-sm cursor-pointer block"
                >
                  {{ $t(`leftBar.${item.displayName || item.name}`) }}
                </a>
              </span>
            </div>
          </router-link>
        </template>
      </template>
    </li>
    <slot name="footer" />
  </ul>
</template>

<script>
import icon from '@components/icons/icon.vue'
import { mapActions, mapState } from 'vuex'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import { FEATURES } from '@src/constants/general-constants.js'
import { FEATURES_ENVIRONMENT, ALLOWED_FEATURES } from '@src/services/.env.js'
import isArray from 'lodash/isArray'

export default {
  components: { icon },

  props: {
    content: { type: Array, default: () => [] },
    leftBarVisibility: { type: Boolean, default: false },
  },
  emits: ['setcurrentSelectedSection', 'setSelectedCampuse'],

  data() {
    return {
      isAppInitialised: false,
      role: 'admin dashboard',
      currentHoverItem: '',
      pickupRoutes: ['facility-active-request', 'all-request'],
      isTabLeftBar: window.matchMedia('(max-width: 767px)').matches,
    }
  },

  computed: {
    ...mapState('layout', [
      'activeRole',
      'currentSectionScope',
      'tabLeftNav',
      'currentCampusScope',
      'currentClassScope',
      'currentRoute',
      'disabledFeature',
    ]),
    ...mapState({
      isStudentDispatcher: (state) => state.settings.isStudentDispatcher,
    }),

    featureListFiltered() {
      if (!FEATURES_ENVIRONMENT) return this.content

      return this.content
        .map((item) => {
          if (item.childs?.length) {
            const childs = item.childs.filter((child) => ALLOWED_FEATURES.includes(child.name))
            return childs.length ? { ...item, childs } : null
          }
          return ALLOWED_FEATURES.includes(item.name) ? item : null
        })
        .filter(Boolean)
    },
  },

  watch: {
    featureListFiltered: {
      deep: true,
      handler() {
        if (!this.isAppInitialised) this.toggleInitialLeftbarCollapseState()
      },
    },
  },

  methods: {
    isMainFeatureEnable(route) {
      const isDisabled = this.disabledFeature?.includes(route.name)
      const children = isArray(route.childs) ? route.childs : []

      if (isDisabled) return children.some((child) => child.isMandatory)
      if (!children.length) return true
      return children.some(
        (child) => this.childRoleAccess(child) && !this.disabledFeature?.includes(child.name),
      )
    },

    isSubFeatureEnable(item, child) {
      if (this.disabledFeature?.includes(child.name)) return false
      if (this.disabledFeature?.includes(item.name)) return !!child.isMandatory
      return true
    },

    showLeftBarItem(item) {
      return (
        (item.role?.includes(this.activeRole) || item.isRenderForcefully) && this.leftBarVisibility
      )
    },

    showItems(item) {
      const isPickupBlocked =
        [TENANT_ROLES.PICKUP_PERSON, TENANT_ROLES.GUARDIAN].includes(this.activeRole) &&
        item.icon === FEATURES.PICKUP &&
        !this.isStudentDispatcher

      return !isPickupBlocked && (item.role?.includes(this.activeRole) || item.isRenderForcefully)
    },

    childItemsClasses(item) {
      return !this.isStudentDispatcher && item.icon === FEATURES.PICKUP
        ? 'text-warning-500'
        : 'hover:text-primary-purple-700 text-primary-purple-400'
    },

    childItemActive(item) {
      return !this.isStudentDispatcher && item.icon === FEATURES.PICKUP
        ? 'text-warning-500'
        : 'text-primary-purple-700'
    },

    setIconColor(item, activeCell = false) {
      if (!this.isStudentDispatcher && item.icon === FEATURES.PICKUP) {
        return 'warning-500'
      }
      if (
        item?.id === this.currentHoverItem?.id ||
        activeCell ||
        item.showChilds ||
        item?.routesName?.includes(this.currentRoute)
      ) {
        return 'primary-purple-700'
      }
      return 'primary-gray-700'
    },

    toggleLeftBar() {
      if (this.isTabLeftBar) this.$store.commit('layout/SET_TAB_LEFT_BAR')
    },

    childRoleAccess(child) {
      return (
        (child.route && !child.isDynamicRoute && child.role?.includes(this.activeRole)) ||
        (child.route && !child.isDynamicRoute && !child.role)
      )
    },

    toggleInitialLeftbarCollapseState() {
      this.isAppInitialised = true
      const matchedRouteParent = this.featureListFiltered.find((route) =>
        route?.routesName?.includes(this.$route.name),
      )
      if (matchedRouteParent?.childs?.length) this.toggleChilds(matchedRouteParent)
    },

    toggleChilds(item) {
      const parentItem = FEATURES_ENVIRONMENT && this.content.find((p) => p.name === item.name)
      this.setItemState(parentItem || item)
    },

    closeLeftBar(route, item) {
      this.$router?.push(route)
      this.toggleLeftBar()
    },

    setTextColor(isActive, item) {
      const activeItem = isActive || this.makeLeftbarParentActive(item)

      if (!this.isStudentDispatcher && item.icon === FEATURES.PICKUP) {
        return activeItem
          ? 'text-warning-500 bg-primary-purple-25'
          : 'text-warning-500 hover:bg-primary-purple-25 hover:text-warning-500'
      }
      return activeItem
        ? 'bg-primary-purple-25 text-primary-purple-700'
        : 'text-primary-gray-700 hover:bg-primary-purple-25 hover:text-primary-purple-700'
    },

    makeLeftbarParentActive(item) {
      return item.routesName?.some((routeName) => routeName === this.$route.name) || false
    },

    childRouteNavigate(routeName, item) {
      this.$router?.push(routeName)
      this.$emit('setcurrentSelectedSection', item)
    },

    routeNavigate(routeName, item) {
      this.$router?.push(routeName)
      this.$emit('setSelectedCampuse', item)
    },

    makeDropdownActive(parent) {
      const activeChild = parent.childs?.some((child) => child.route.name === this.currentRoute)
      const parentChildActive = activeChild || parent.showChilds

      if (!this.isStudentDispatcher && parent.icon === FEATURES.PICKUP) {
        return parentChildActive
          ? 'text-warning-500 bg-primary-purple-25'
          : 'text-warning-500 hover:bg-primary-purple-25'
      }
      return parentChildActive
        ? 'bg-primary-purple-25 text-primary-purple-700'
        : 'text-primary-gray-700 hover:bg-primary-purple-25 hover:text-primary-purple-700'
    },

    ...mapActions('layout', ['setItemState']),
  },
}
</script>

<style lang="scss" scoped>
.dropdown {
  transition: transform 0.1s ease-in-out;
  transform-origin: top;
}
</style>
